/**
 * Teen Patti Multiplayer Lobby
 * Create, join, and manage multiplayer Teen Patti rooms
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Users, RefreshCw, Crown, Coins, Star, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { VARIATIONS } from './teenPattiStore';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Room Card Component
const RoomCard = ({ room, onJoin }) => {
  const variationColors = {
    CLASSIC: 'border-amber-500/30 bg-amber-500/5',
    MUFLIS: 'border-rose-500/30 bg-rose-500/5',
    AK47: 'border-yellow-500/30 bg-yellow-500/5'
  };
  
  const variationBadges = {
    CLASSIC: { text: 'Classic', color: 'bg-amber-500/20 text-amber-400' },
    MUFLIS: { text: 'Muflis', color: 'bg-rose-500/20 text-rose-400' },
    AK47: { text: 'AK47', color: 'bg-yellow-500/20 text-yellow-400' }
  };
  
  const badge = variationBadges[room.variation] || variationBadges.CLASSIC;
  
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`rounded-xl border-2 p-4 ${variationColors[room.variation] || variationColors.CLASSIC}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="text-lg font-bold text-white">#{room.id}</div>
          <span className={`text-xs px-2 py-0.5 rounded-full ${badge.color}`}>
            {badge.text}
          </span>
        </div>
        <div className="flex items-center gap-1 text-slate-400 text-sm">
          <Users className="w-4 h-4" />
          {room.players}/{room.max_players}
        </div>
      </div>
      
      <div className="flex items-center justify-between text-sm mb-3">
        <div className="text-slate-400">
          Boot: <span className="text-amber-400">₹{room.boot}</span>
        </div>
        <div className={`px-2 py-0.5 rounded text-xs ${
          room.game_phase === 'WAITING' ? 'bg-emerald-500/20 text-emerald-400' :
          room.game_phase === 'BETTING' ? 'bg-amber-500/20 text-amber-400' :
          'bg-slate-500/20 text-slate-400'
        }`}>
          {room.game_phase === 'WAITING' ? 'Waiting' : 
           room.game_phase === 'BETTING' ? 'In Progress' : room.game_phase}
        </div>
      </div>
      
      <Button
        onClick={() => onJoin(room.id)}
        disabled={room.players >= room.max_players}
        className="w-full bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400 disabled:opacity-50"
      >
        {room.players >= room.max_players ? 'Full' : 'Join Room'}
      </Button>
    </motion.div>
  );
};

// Create Room Modal
const CreateRoomModal = ({ onClose, onCreate }) => {
  const [variation, setVariation] = useState('CLASSIC');
  const [boot, setBoot] = useState(20);
  const [maxPlayers, setMaxPlayers] = useState(6);
  const [maxPot, setMaxPot] = useState(null);
  
  const handleCreate = () => {
    onCreate({
      variation,
      boot,
      mini_boot: Math.floor(boot / 2),
      max_players: maxPlayers,
      max_pot: maxPot,
      min_buy_in: boot * 25
    });
  };
  
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        className="bg-gradient-to-br from-slate-900 to-slate-800 border-2 border-amber-500 rounded-2xl p-6 max-w-md w-full"
        onClick={e => e.stopPropagation()}
      >
        <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
          <Plus className="w-5 h-5 text-amber-400" />
          Create Room
        </h2>
        
        <div className="space-y-4">
          {/* Variation */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Game Mode</label>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setVariation('CLASSIC')}
                className={`p-2 rounded-lg border-2 text-xs font-semibold transition-all ${
                  variation === 'CLASSIC' 
                    ? 'border-amber-500 bg-amber-500/20 text-amber-400' 
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                Classic
              </button>
              <button
                onClick={() => setVariation('MUFLIS')}
                className={`p-2 rounded-lg border-2 text-xs font-semibold transition-all ${
                  variation === 'MUFLIS' 
                    ? 'border-rose-500 bg-rose-500/20 text-rose-400' 
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                Muflis
              </button>
              <button
                onClick={() => setVariation('AK47')}
                className={`p-2 rounded-lg border-2 text-xs font-semibold transition-all flex items-center justify-center gap-1 ${
                  variation === 'AK47' 
                    ? 'border-yellow-500 bg-yellow-500/20 text-yellow-400' 
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                <Star className="w-3 h-3" /> AK47
              </button>
            </div>
          </div>
          
          {/* Boot amount */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Boot Amount</label>
            <div className="grid grid-cols-4 gap-2">
              {[10, 20, 50, 100].map(amt => (
                <button
                  key={amt}
                  onClick={() => setBoot(amt)}
                  className={`p-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                    boot === amt 
                      ? 'border-amber-500 bg-amber-500/20 text-amber-400' 
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  ₹{amt}
                </button>
              ))}
            </div>
          </div>
          
          {/* Max players */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Max Players</label>
            <div className="grid grid-cols-3 gap-2">
              {[4, 5, 6].map(n => (
                <button
                  key={n}
                  onClick={() => setMaxPlayers(n)}
                  className={`p-2 rounded-lg border-2 text-sm font-semibold transition-all ${
                    maxPlayers === n 
                      ? 'border-cyan-500 bg-cyan-500/20 text-cyan-400' 
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  {n} Players
                </button>
              ))}
            </div>
          </div>
          
          {/* Pot limit */}
          <div>
            <label className="text-sm text-slate-400 block mb-2">Pot Limit</label>
            <div className="grid grid-cols-4 gap-2">
              <button
                onClick={() => setMaxPot(null)}
                className={`p-2 rounded-lg border-2 text-xs font-semibold transition-all ${
                  maxPot === null 
                    ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
                    : 'border-slate-600 text-slate-400 hover:border-slate-500'
                }`}
              >
                None
              </button>
              {[500, 1000, 2000].map(limit => (
                <button
                  key={limit}
                  onClick={() => setMaxPot(limit)}
                  className={`p-2 rounded-lg border-2 text-xs font-semibold transition-all ${
                    maxPot === limit 
                      ? 'border-emerald-500 bg-emerald-500/20 text-emerald-400' 
                      : 'border-slate-600 text-slate-400 hover:border-slate-500'
                  }`}
                >
                  ₹{limit}
                </button>
              ))}
            </div>
          </div>
        </div>
        
        <div className="flex gap-3 mt-6">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-slate-600 text-slate-300"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            className="flex-1 bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400"
          >
            Create Room
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

// Main Lobby Component
const TeenPattiLobby = () => {
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [playerName, setPlayerName] = useState('');
  
  // Generate player ID
  const [playerId] = useState(() => {
    const stored = localStorage.getItem('teenpatti_player_id');
    if (stored) return stored;
    const newId = `TP_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('teenpatti_player_id', newId);
    return newId;
  });
  
  // Load player name
  useEffect(() => {
    const stored = localStorage.getItem('teenpatti_player_name');
    if (stored) setPlayerName(stored);
  }, []);
  
  // Save player name
  const updatePlayerName = (name) => {
    setPlayerName(name);
    localStorage.setItem('teenpatti_player_name', name);
  };
  
  // Fetch rooms
  const fetchRooms = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/teenpatti/rooms`);
      const data = await res.json();
      setRooms(data.rooms || []);
    } catch (err) {
      console.error('Failed to fetch rooms:', err);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    fetchRooms();
    const interval = setInterval(fetchRooms, 5000); // Refresh every 5s
    return () => clearInterval(interval);
  }, []);
  
  // Create room
  const handleCreateRoom = async (config) => {
    try {
      const res = await fetch(`${API_URL}/api/teenpatti/rooms`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(config)
      });
      const data = await res.json();
      
      if (data.room_id) {
        navigate(`/games/teen-patti/room/${data.room_id}`, {
          state: { playerId, playerName: playerName || 'Host', config, isHost: true }
        });
      }
    } catch (err) {
      console.error('Failed to create room:', err);
    }
    setShowCreateModal(false);
  };
  
  // Join room
  const handleJoinRoom = (roomId) => {
    if (!playerName) {
      const name = prompt('Enter your name:');
      if (name) updatePlayerName(name);
      else return;
    }
    
    navigate(`/games/teen-patti/room/${roomId}`, {
      state: { playerId, playerName }
    });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-amber-950/20 to-slate-950">
      {/* Header */}
      <div className="bg-slate-900/80 backdrop-blur-sm border-b-2 border-amber-700/50 px-4 py-3">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/games/teen-patti')}
              className="text-slate-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-white">Teen Patti Lobby</h1>
              <p className="text-xs text-amber-400">Find or create a room</p>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={playerName}
              onChange={(e) => updatePlayerName(e.target.value)}
              placeholder="Your name"
              className="bg-slate-800 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white placeholder:text-slate-500 w-32"
            />
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-500 hover:to-amber-400"
            >
              <Plus className="w-4 h-4 mr-2" />
              Create Room
            </Button>
          </div>
        </div>
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto p-6">
        {/* Quick join buttons */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-white">Active Rooms</h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={fetchRooms}
            className="text-slate-400 hover:text-white"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
        
        {/* Rooms grid */}
        {loading && rooms.length === 0 ? (
          <div className="text-center py-12 text-slate-400">
            Loading rooms...
          </div>
        ) : rooms.length === 0 ? (
          <div className="text-center py-12">
            <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <div className="text-slate-400 mb-4">No active rooms</div>
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-gradient-to-r from-amber-600 to-amber-500"
            >
              Create the first room
            </Button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {rooms.map(room => (
              <RoomCard key={room.id} room={room} onJoin={handleJoinRoom} />
            ))}
          </div>
        )}
        
        {/* Play vs AI link */}
        <div className="mt-8 text-center">
          <div className="text-slate-500 text-sm mb-2">or</div>
          <Button
            variant="outline"
            onClick={() => navigate('/games/teen-patti')}
            className="border-slate-600 text-slate-300"
          >
            <Zap className="w-4 h-4 mr-2" />
            Play vs AI
          </Button>
        </div>
      </div>
      
      {/* Create Room Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <CreateRoomModal 
            onClose={() => setShowCreateModal(false)} 
            onCreate={handleCreateRoom}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default TeenPattiLobby;
